
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export default function XeroCallback() {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const error = params.get('error');

    if (error) {
      setStatus('error');
      toast({
        title: 'Authentication Failed',
        description: 'Failed to authenticate with Xero: ' + error,
        variant: 'destructive',
      });
      return;
    }

    if (code) {
      // Here you would typically exchange the code for an access token
      // For this demo, we'll simulate a successful authentication
      setTimeout(() => {
        setStatus('success');
        toast({
          title: 'Xero Connected',
          description: 'Your Xero account has been successfully connected.',
        });
      }, 1500);
    } else {
      setStatus('error');
      toast({
        title: 'Authentication Failed',
        description: 'No authorization code received from Xero',
        variant: 'destructive',
      });
    }
  }, [location.search, toast]);

  const goToSettings = () => {
    navigate('/settings?tab=integrations');
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-background p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6 flex flex-col items-center text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="h-12 w-12 text-primary animate-spin mb-4" />
              <h2 className="text-xl font-semibold mb-2">Connecting to Xero</h2>
              <p className="text-muted-foreground mb-4">Please wait while we complete the authentication...</p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <CheckCircle className="h-12 w-12 text-green-500 mb-4" />
              <h2 className="text-xl font-semibold mb-2">Successfully Connected!</h2>
              <p className="text-muted-foreground mb-6">Your Xero account has been successfully connected.</p>
              <Button onClick={goToSettings}>Go to Settings</Button>
            </>
          )}
          
          {status === 'error' && (
            <>
              <XCircle className="h-12 w-12 text-destructive mb-4" />
              <h2 className="text-xl font-semibold mb-2">Connection Failed</h2>
              <p className="text-muted-foreground mb-6">There was a problem connecting to your Xero account.</p>
              <Button onClick={goToSettings} variant="outline">Back to Settings</Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
